import { Alert, Anchor, Box, Button, Stack, Text, Title } from '@mantine/core';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { AuthShell } from '../components/auth-shell/AuthShell';
import { mutateJson } from '../lib/api';
import { useAuth } from '../lib/auth-context';

const verifySearchSchema = z.object({
  token: z.string().min(1).max(500).optional(),
});

type VerifyEmailResponse = { ok: boolean; alreadyVerified: boolean };

type Status = 'checking' | 'success' | 'error' | 'missing_token';

function VerifyEmailScreen() {
  const search = useSearchToken();
  const { refresh } = useAuth();
  const [status, setStatus] = useState<Status>(search === undefined ? 'missing_token' : 'checking');
  const [message, setMessage] = useState<string | null>(null);
  // Guard against React 18 dev double-invoke / a re-render re-firing the
  // one-shot consume call — the token is single-use, so a second POST would
  // otherwise surface a misleading "expired or already used" error for what
  // is actually the app's own retry.
  const firedRef = useRef(false);

  useEffect(() => {
    if (search === undefined || firedRef.current) return;
    firedRef.current = true;
    (async () => {
      try {
        await mutateJson<VerifyEmailResponse>('/api/auth/verify-email', { token: search });
        setStatus('success');
        // Refresh /me so a signed-in caller's status flips from 'pending' to
        // 'active' without a full reload (unblocks requireActiveAccount-gated
        // actions immediately).
        await refresh();
      } catch (err) {
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Could not confirm this email.');
      }
    })().catch(() => {
      // mutateJson already surfaces its own error via the catch above; this
      // outer catch only exists to satisfy the no-floating-promise rule.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on mount for this token
  }, [search]);

  return (
    <AuthShell>
      <Stack gap="xl">
        <Box>
          <Text size="xs" tt="uppercase" c="brand.7" fw={600} style={{ letterSpacing: '0.1em' }}>
            Confirm your email
          </Text>
          <Title
            order={1}
            mt={6}
            style={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.1rem)', lineHeight: 1.15, letterSpacing: '-0.015em' }}
          >
            {status === 'success' ? "You're confirmed." : 'Confirming your email.'}
          </Title>
        </Box>

        {status === 'missing_token' && (
          <Alert color="red" variant="light">
            That link is missing its confirmation code. Request a fresh one from Settings, or sign
            in and use the resend link there.
          </Alert>
        )}
        {status === 'checking' && (
          <Text c="ink.6" style={{ lineHeight: 1.55 }}>
            One moment.
          </Text>
        )}
        {status === 'success' && (
          <Alert color="tide" variant="light">
            Your email is confirmed. You can use every part of WWAAVE now.
          </Alert>
        )}
        {status === 'error' && (
          <Alert color="red" variant="light">
            {message ?? 'That confirmation link has expired or already been used.'}
          </Alert>
        )}

        <Stack gap="xs">
          <Button component={Link} to={status === 'success' ? '/dashboard' : '/sign-in'} fullWidth size="md" variant="gradient" gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}>
            {status === 'success' ? 'Go to your dashboard' : 'Back to sign in'}
          </Button>
          <Text size="sm" ta="center">
            <Anchor component={Link} to="/settings/account" c="brand.7">
              Resend from Settings
            </Anchor>
          </Text>
        </Stack>
      </Stack>
    </AuthShell>
  );
}

/** Thin wrapper so the effect's dependency is a stable primitive, not the search object. */
function useSearchToken(): string | undefined {
  const search = Route.useSearch();
  return search.token;
}

export const Route = createFileRoute('/verify-email')({
  validateSearch: verifySearchSchema,
  component: VerifyEmailScreen,
});
